import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';

export interface BrandItem {
  code: string;
  name: string;
  kind: 'shop' | 'media';
  domains: string[];
  sender: { email: string; name: string; replyTo?: string };
  senderGroups?: Record<string, { email: string; name: string; replyTo?: string } | undefined>;
  providerAccount: string | null;
  languages: string[];
  defaultLanguage: string;
  defaultTimezone: string;
  support: Record<string, unknown>;
  watchdogUrls: Record<string, Partial<Record<string, string>>>;
  fieldPrefix: string | null;
  sendingEnabled: boolean;
  /** Výslovně vybraný správce souhlasů; `null` = nevybrán. */
  consentControllerSet: string | null;
  /** Jsou vyplněné údaje, bez nichž nejde zapnout odesílání? */
  readiness: { ready: boolean; missing: string[] };
  credentials: 'brand' | 'default' | 'none';
  sharedAccount: boolean;
  settings: {
    windows: {
      value: {
        standard: { fromHour: number; toHour: number };
        critical: { fromHour: number; toHour: number };
      };
      overridden: boolean;
    };
    frequencyCapDays: { value: number; overridden: boolean };
  };
}

export function useBrands() {
  return useQuery<{ items: BrandItem[] }>({
    queryKey: ['brands'],
    queryFn: () => api.get('/api/brands'),
    staleTime: 60_000,
  });
}

/** Filtr značky do hlaviček obrazovek; omezený uživatel dostane jen své značky. */
export function BrandSelect({
  value,
  onChange,
  allLabel = 'Všechny značky',
  shopOnly = false,
}: {
  value: string;
  onChange: (code: string) => void;
  allLabel?: string | null;
  /** Jen značky typu shop (šablony, weby). */
  shopOnly?: boolean;
}): JSX.Element {
  const { data } = useBrands();
  const items = (data?.items ?? []).filter((b) => !shopOnly || b.kind === 'shop');
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Značka">
      {allLabel !== null && items.length !== 1 ? <option value="">{allLabel}</option> : null}
      {items.map((b) => (
        <option key={b.code} value={b.code}>
          {b.name}
        </option>
      ))}
    </select>
  );
}
